import { useState, useMemo } from 'react';
import { useStore } from '../../data/store';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { Music, ExternalLink, Guitar, Globe, Instagram, Facebook, Mail, X, Play, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { api } from '../../data/api';

function formatDayLabel(isoDate) {
  if (!isoDate) return isoDate;
  const d = new Date(isoDate + 'T00:00:00');
  if (isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
}

function formatDayShort(isoDate) {
  if (!isoDate) return '—';
  const d = new Date(isoDate + 'T00:00:00');
  if (isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('de-DE', { weekday: 'long' }).toUpperCase();
}

function hasBandProfile(band) {
  return Boolean(
    band?.bio ||
    band?.description ||
    band?.musicUrl ||
    band?.websiteUrl ||
    band?.playlistUrl ||
    band?.instagramUrl ||
    band?.facebookUrl ||
    band?.contactInfo
  );
}

export default function Lineup() {
  const event = useStore((s) => s.event);
  const bands = useStore((s) => s.bands);
  const [genreFilter, setGenreFilter] = useState('ALLE');
  const [dayFilter, setDayFilter] = useState('ALLE');
  const [sortMode, setSortMode] = useState('date');
  const [selectedBand, setSelectedBand] = useState(null);
  const [ref, isVisible] = useScrollAnimation();

  const activeBands = useMemo(() => (bands || []).filter((b) => b.active), [bands]);

  const availableGenres = useMemo(() => {
    const set = new Set(activeBands.map((b) => (b.genre || '').toUpperCase()).filter(Boolean));
    return ['ALLE', ...Array.from(set).sort()];
  }, [activeBands]);

  const availableDays = useMemo(() => {
    const set = new Set(activeBands.map((b) => b.day).filter(Boolean));
    return ['ALLE', ...Array.from(set).sort()];
  }, [activeBands]);

  const filteredBands = useMemo(() => {
    let result = activeBands;
    if (genreFilter !== 'ALLE') result = result.filter((b) => (b.genre || '').toUpperCase() === genreFilter);
    if (dayFilter !== 'ALLE') result = result.filter((b) => b.day === dayFilter);
    result = [...result].sort((a, b) => {
      if (sortMode === 'date') {
        const dayCmp = (a.day || '').localeCompare(b.day || '');
        if (dayCmp !== 0) return dayCmp;
        return (a.time || '').localeCompare(b.time || '');
      }
      const cmp = (a.name || '').localeCompare(b.name || '', 'de');
      return sortMode === 'name-asc' ? cmp : -cmp;
    });
    return result;
  }, [activeBands, genreFilter, dayFilter, sortMode]);

  const timetableDays = useMemo(() => {
    const set = new Set(activeBands.map((b) => b.day).filter(Boolean));
    return Array.from(set).sort();
  }, [activeBands]);

  const showDayFilter = availableDays.length > 2;

  return (
    <section id="lineup" className="bg-bg-primary section-padding min-h-[600px]">
      <div
        ref={ref}
        className={`max-w-7xl mx-auto transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="text-center mb-12 md:mb-16">
          <p className="font-heading text-accent-red text-sm tracking-[4px] mb-4">DIE BANDS</p>
          <h2 className="font-heading text-text-primary" style={{ fontSize: 'clamp(28px, 7vw, 56px)' }}>
            LINEUP {event?.name ? event.name.replace(/[^0-9]/g, '') || '2026' : '2026'}
          </h2>
        </div>

        {event?.lineupPublished ? (
          <>
            <div className="flex flex-col items-center gap-4 mb-10 md:mb-14">
              <div className="flex flex-wrap justify-center gap-2">
                {availableGenres.map((g) => (
                  <button
                    key={g}
                    onClick={() => setGenreFilter(g)}
                    className={`font-heading text-sm tracking-wider px-4 py-2.5 border-2 transition-all min-h-[44px] ${
                      genreFilter === g
                        ? 'bg-accent-red border-accent-red text-white'
                        : 'border-divider text-text-muted hover:border-accent-red hover:text-accent-red'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>

              {showDayFilter && (
                <div className="flex flex-wrap justify-center gap-2">
                  {availableDays.map((d) => (
                    <button
                      key={d}
                      onClick={() => setDayFilter(d)}
                      className={`font-body text-xs tracking-wider px-4 py-2 border transition-all min-h-[36px] ${
                        dayFilter === d
                          ? 'bg-accent-red/20 border-accent-red text-accent-red'
                          : 'border-divider/50 text-text-muted hover:border-accent-red hover:text-text-primary'
                      }`}
                    >
                      {d === 'ALLE' ? 'ALLE TAGE' : formatDayShort(d)}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                {[
                  { key: 'date', label: 'Nach Datum' },
                  { key: 'name-asc', label: 'A – Z' },
                  { key: 'name-desc', label: 'Z – A' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setSortMode(key)}
                    className={`font-body text-xs px-3 py-1.5 border transition-all ${
                      sortMode === key
                        ? 'border-accent-red text-accent-red bg-accent-red/10'
                        : 'border-divider/50 text-text-muted hover:border-accent-red hover:text-text-primary'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {filteredBands.length === 0 ? (
              <p className="text-text-muted text-center font-body py-12">Keine Bands für diese Auswahl.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {filteredBands.map((band) => (
                  <BandCard
                    key={band.id}
                    band={band}
                    profileAvailable={hasBandProfile(band)}
                    onOpenProfile={() => setSelectedBand(band)}
                  />
                ))}
              </div>
            )}

            {event.timetablePublished && timetableDays.length > 0 && (
              <div className="mt-16 md:mt-20">
                <h3 className="font-heading text-2xl md:text-3xl text-text-primary text-center mb-8 md:mb-10">TIMETABLE</h3>
                <div className={`grid gap-6 md:gap-8 ${timetableDays.length > 1 ? 'md:grid-cols-2' : ''}`}>
                  {timetableDays.map((day) => (
                    <TimetableColumn
                      key={day}
                      title={formatDayShort(day)}
                      bands={activeBands.filter((b) => b.day === day).sort((a, b) => (a.time || '').localeCompare(b.time || ''))}
                      onOpenProfile={(b) => hasBandProfile(b) && setSelectedBand(b)}
                    />
                  ))}
                </div>
              </div>
            )}

            {event.timetablePublished && timetableDays.length === 0 && (
              <div className="mt-16 md:mt-20 text-center py-10 md:py-12 bg-bg-secondary border border-divider">
                <p className="font-heading text-xl md:text-2xl text-text-muted">TIMETABLE COMING SOON</p>
              </div>
            )}

            {!event.timetablePublished && (
              <div className="mt-16 md:mt-20 text-center py-10 md:py-12 bg-bg-secondary border border-divider">
                <p className="font-heading text-xl md:text-2xl text-text-muted">TIMETABLE COMING SOON</p>
              </div>
            )}
          </>
        ) : (
          <ComingSoon />
        )}
      </div>

      {selectedBand && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          onClick={() => setSelectedBand(null)}
        >
          <div
            className="bg-bg-card border border-divider w-full max-w-lg max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-6 border-b border-divider">
              <div>
                <h3 className="font-heading text-2xl text-text-primary">{selectedBand.name}</h3>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="inline-block font-body text-xs text-text-muted border border-divider px-3 py-1">
                    {selectedBand.genre}
                  </span>
                  {selectedBand.headliner && (
                    <span className="inline-block font-heading text-xs tracking-wider text-accent-gold border border-accent-gold px-3 py-1">
                      HEADLINER
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => setSelectedBand(null)} className="text-text-muted hover:text-text-primary transition-colors p-1 ml-4 shrink-0">
                <X size={22} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <p className="font-body text-text-muted text-sm">
                {selectedBand.day ? formatDayLabel(selectedBand.day) : ''}{selectedBand.time ? ` · ${selectedBand.time} Uhr` : ''}{selectedBand.duration ? ` · ${selectedBand.duration} Min.` : ''}
              </p>

              {(selectedBand.description || selectedBand.bio) && (
                <p className="font-body text-text-primary text-sm leading-relaxed">
                  {selectedBand.description || selectedBand.bio}
                </p>
              )}

              <div className="space-y-3 pt-2">
                {selectedBand.playlistUrl && (
                  <ProfileLink icon={<Play size={15} />} label="Musik hören" href={selectedBand.playlistUrl} />
                )}
                {selectedBand.musicUrl && !selectedBand.playlistUrl && (
                  <ProfileLink icon={<Music size={15} />} label="Musik hören" href={selectedBand.musicUrl} />
                )}
                {selectedBand.websiteUrl && (
                  <ProfileLink icon={<Globe size={15} />} label="Website" href={selectedBand.websiteUrl} />
                )}
                {selectedBand.instagramUrl && (
                  <ProfileLink icon={<Instagram size={15} />} label="Instagram" href={selectedBand.instagramUrl} />
                )}
                {selectedBand.facebookUrl && (
                  <ProfileLink icon={<Facebook size={15} />} label="Facebook" href={selectedBand.facebookUrl} />
                )}
                {selectedBand.contactInfo && (
                  <div className="flex items-center gap-3">
                    <span className="text-accent-red shrink-0"><Mail size={15} /></span>
                    <span className="font-body text-text-muted text-sm">{selectedBand.contactInfo}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function BandCard({ band, profileAvailable, onOpenProfile }) {
  return (
    <div
      role={profileAvailable ? 'button' : undefined}
      tabIndex={profileAvailable ? 0 : undefined}
      aria-label={profileAvailable ? `${band.name} Profil ansehen` : undefined}
      onClick={() => profileAvailable && onOpenProfile()}
      onKeyDown={(e) => {
        if (profileAvailable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onOpenProfile();
        }
      }}
      className={`group relative bg-bg-card border-t-4 p-6 md:p-8 transition-transform duration-300 hover:scale-[1.03] ${
        band.headliner ? 'border-t-accent-gold' : 'border-t-accent-red'
      } ${profileAvailable ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-bg-secondary flex items-center justify-center shrink-0 mt-1">
          <Music size={24} className="text-accent-red" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-heading leading-tight ${band.headliner ? 'text-3xl text-accent-gold' : 'text-2xl text-text-primary'}`}>
            {band.name}
          </h3>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="inline-block font-body text-xs text-text-muted border border-divider px-3 py-1">
              {band.genre}
            </span>
            {band.headliner && (
              <span className="inline-block font-heading text-xs tracking-wider text-accent-gold border border-accent-gold px-3 py-1">
                HEADLINER
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 max-h-0 overflow-hidden group-hover:max-h-40 transition-all duration-300">
        {band.bio && <p className="font-body text-text-muted text-sm leading-relaxed mb-2">{band.bio}</p>}
        {profileAvailable && (
          <span className="inline-flex items-center gap-1 text-accent-red text-sm mt-2 font-heading tracking-wide">
            Profil ansehen <ExternalLink size={14} />
          </span>
        )}
      </div>

      {(band.day || band.time) && (
        <div className="mt-4 text-text-muted text-sm font-body">
          {band.day ? formatDayShort(band.day) : ''}{band.time ? ` · ${band.time} Uhr` : ''}
        </div>
      )}
    </div>
  );
}

function TimetableColumn({ title, bands, onOpenProfile }) {
  return (
    <div className="bg-bg-card p-6 md:p-8">
      <h4 className="font-heading text-2xl text-accent-red mb-6 md:mb-8 text-center border-b border-divider pb-4 md:pb-5">{title}</h4>
      <div className="space-y-2">
        {bands.map((band) => {
          const profileAvailable = hasBandProfile(band);
          return (
            <div
              key={band.id}
              role={profileAvailable ? 'button' : undefined}
              tabIndex={profileAvailable ? 0 : undefined}
              aria-label={profileAvailable ? `${band.name} Profil ansehen` : undefined}
              className={`flex items-center gap-3 md:gap-4 py-3 border-b border-divider/50 last:border-0 ${profileAvailable ? 'cursor-pointer hover:bg-white/5 transition-colors rounded' : ''}`}
              onClick={() => profileAvailable && onOpenProfile(band)}
              onKeyDown={(e) => {
                if (profileAvailable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onOpenProfile(band);
                }
              }}
            >
              <span className="font-heading text-lg md:text-xl text-accent-red w-14 md:w-16 shrink-0">{band.time}</span>
              <div className="flex-1 min-w-0">
                <span className={`font-heading text-base md:text-lg ${band.headliner ? 'text-accent-gold' : 'text-text-primary'}`}>
                  {band.name}
                </span>
                {band.headliner && (
                  <span className="ml-2 font-heading text-xs text-accent-gold opacity-70">★</span>
                )}
                <span className="text-text-muted text-xs ml-2">({band.duration} Min.)</span>
              </div>
              <span className="text-text-muted text-xs border border-divider px-2 md:px-3 py-1 shrink-0 hidden sm:inline">{band.genre}</span>
              {profileAvailable && <ExternalLink size={12} className="text-text-muted shrink-0" />}
            </div>
          );
        })}
        {bands.length === 0 && <p className="text-text-muted text-center py-4">Keine Bands geplant</p>}
      </div>
    </div>
  );
}

function ProfileLink({ icon, label, href }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm font-body text-accent-red hover:underline group">
      <span className="shrink-0">{icon}</span>
      <span>{label}</span>
      <ExternalLink size={12} className="opacity-50 group-hover:opacity-100 transition-opacity" />
    </a>
  );
}

function ComingSoon() {
  const bands = useStore((s) => s.bands);
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.public.subscribe(email);
      setMsg('Danke! Du wirst benachrichtigt.');
      setEmail('');
    } catch (err) {
      setMsg(err.message || 'Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  const revealDays = Math.floor((new Date('2026-05-01').getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  return (
    <div className="text-center">
      <div className="flex flex-wrap justify-center gap-3 mb-8 max-w-md mx-auto">
        {['70%', '55%', '80%', '45%', '65%', '50%'].map((w, i) => (
          <div
            key={i}
            style={{
              width: w, height: 32, background: 'rgba(26,95,180,0.15)',
              borderRadius: 2, filter: 'blur(6px)',
              animation: `pulse-bar 2s ease-in-out ${i * 0.3}s infinite`,
            }}
          />
        ))}
      </div>

      <h3 className="font-heading text-2xl md:text-3xl text-text-primary mb-2">NOCH GEHEIM. BALD LAUT.</h3>
      <p className="font-body text-text-muted mb-10 max-w-md mx-auto text-base">
        Wir veröffentlichen die ersten Bands in Kürze.
      </p>

      {revealDays > 0 && (
        <div className="inline-flex items-center gap-2 bg-accent-red/10 border border-accent-red/30 px-5 py-2.5 rounded-full mb-10">
          <Guitar size={18} className="text-accent-red" />
          <span className="font-body text-accent-red text-sm">
            Lineup-Reveal in <strong>{revealDays} Tagen</strong>
          </span>
        </div>
      )}

      <div className="max-w-md mx-auto mb-12">
        <p className="font-body text-text-muted text-sm mb-4">Als Erstes erfahren:</p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="deine@email.de" required
            className="flex-1 bg-bg-card border border-divider px-4 py-3 text-text-primary font-body text-base placeholder:text-text-muted focus:outline-none focus:border-accent-red min-h-[48px]"
          />
          <button type="submit" disabled={submitting} className="bg-accent-red text-white font-heading tracking-wider px-6 py-3 hover:bg-transparent hover:border-accent-red border-2 border-accent-red hover:text-accent-red transition-all min-h-[48px] disabled:opacity-50">
            {submitting ? '...' : 'ANMELDEN'}
          </button>
        </form>
        {msg && <p className="text-accent-red font-body text-sm mt-4">{msg}</p>}
      </div>

      {(bands || []).length > 0 && (
        <div className="border-t border-divider pt-10 mt-10">
          <p className="font-heading text-xs tracking-[4px] text-text-muted mb-3 uppercase">Rückblick</p>
          <h3 className="font-heading text-lg md:text-xl text-text-muted/60 mb-8">DIESE BANDS WAREN DABEI</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-2xl mx-auto opacity-60">
            {(bands || []).slice(0, 6).map((band) => (
              <div key={band.id} className="bg-bg-card border border-divider p-4 text-center">
                <p className="font-heading text-base text-text-primary">{band.name}</p>
                <p className="font-body text-text-muted text-xs mt-1">{band.genre}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
